import {
    Button,
    buttonClasses,
    MenuItemProps,
    Size,
    SplitButton,
    splitButtonClasses,
} from "@kaltura/ds-react-components";
import { Calendar24Icon, Mail24Icon, Rsvp24Icon } from "@kaltura/ds-react-icons";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { Children, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { AddToCalendarMenuItems, StyledMenuItem } from "../add-to-calendar-items/AddToCalendarMenuItems";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { RsvpService } from "./RsvpService";
import styled from "@emotion/styled";
import { Theme } from "@mui/material";

export enum RsvpStatus {
    UNCONFIRMED,
    CONFIRMED,
}

export interface RsvpButtonProps {
    entry: { id: string; name: string };
    ks?: string;
    initialStatus?: RsvpStatus;
    size?: Size;
    fetchStatusOnMount?: boolean;
    allowResendPostRsvp?: boolean;
    analyticsValue?: { value?: string; location?: string };
    onChange?: (status: RsvpStatus) => void;
    isLoading?: boolean;
    onToggleClicked?: () => void;
    calendarEntriesToken?: string;
}

const getLabelMinWidthStyle = (length: number, theme: Theme) => ({
    // We add 24px for icon + theme.spacing(1) for padding (between text and icon).
    minWidth: `calc(${length}ch + ${theme.spacing(1)} + 24px)`,
});

const StyledSplitButton = styled(
    SplitButton,
    {
        shouldForwardProp: (prop) => prop !== "maxLabelChars",
    }
)<{ maxLabelChars: number }>(({ theme, maxLabelChars, loading }) => ({
    // target the label span inside the SplitButton's action button and apply a minimum width.
    [`.${splitButtonClasses.actionButton} .${buttonClasses.label}`]: getLabelMinWidthStyle(maxLabelChars, theme),
}));

const StyledButton = styled(
    Button,
    {
        shouldForwardProp: (prop) => prop !== "maxLabelChars",
    }
)<{ maxLabelChars: number }>(({ theme, maxLabelChars }) => ({
    [`.${buttonClasses.label}`]: getLabelMinWidthStyle(maxLabelChars, theme),
}));

/**
 * RSVP Button Component
 *
 * This component renders either a SplitButton or a regular Button
 * to perform RSVP actions such as "RSVP" or "Cancel RSVP",
 * For "Cancel RSVP" action the text on a button will be "RSVP accepted" an on hover text will be changed into "Cancel RSVP".
 *
 * Problem:
 * - When the label changes (e.g., on hover), and the text length varies, the button width flickers.
 * - This results in a jarring layout shift that degrades user experience.
 *
 * Solution:
 * - We compute the longest possible label (including hover states).
 * - We set a `min-width` on the button label container to reserve enough space for any label.
 *
 * The width computation includes an estimated width for icons and padding (24px + theme spacing).
 */
export const RsvpButton = ({
    entry,
    ks,
    size,
    initialStatus = RsvpStatus.UNCONFIRMED,
    fetchStatusOnMount = false,
    allowResendPostRsvp,
    onChange,
    analyticsValue,
    isLoading,
    onToggleClicked,
    calendarEntriesToken,
}: RsvpButtonProps) => {
    const [currentStatus, setCurrentStatus] = useState(initialStatus);
    const [processing, setProcessing] = useState<boolean>();

    const config = useConfig();
    const { showToast } = useToastsContext();

    const rsvpLabel = translate("RSVP");
    const rsvpAcceptedLabel = translate("RSVP accepted");
    const cancelRsvpLabel = translate("Cancel RSVP");
    const baseClassName = "kms-ds-rsvp-button";

    const textIconMappingByStatus = useMemo(
        () => ({
            [RsvpStatus.UNCONFIRMED]: { label: rsvpLabel, icon: <Calendar24Icon /> },
            [RsvpStatus.CONFIRMED]: {
                label: rsvpAcceptedLabel,
                icon: <Rsvp24Icon />,
            },
        }),
        []
    );

    const [buttonLabel, setButtonLabel] = useState(textIconMappingByStatus[currentStatus].label);

    useEffect(() => {
        setButtonLabel(textIconMappingByStatus[currentStatus].label);
    }, [currentStatus, textIconMappingByStatus]);

    const getSessionsRsvpStatus = useCallback(async () => {
        setProcessing(true);
        const rsvpStatusResult = await RsvpService.getRsvpStatus(entry.id);
        setCurrentStatus(rsvpStatusResult);
        setProcessing(false);
    }, [entry.id]);

    useEffect(() => {
        if (fetchStatusOnMount && config.rsvp?.isCrossSite) {
            config.application.isLoggedIn ? getSessionsRsvpStatus() : setCurrentStatus(RsvpStatus.UNCONFIRMED);
        }
    }, [
        fetchStatusOnMount,
        entry.id,
        ks,
        onChange,
        config.application.isLoggedIn,
        getSessionsRsvpStatus,
        config.rsvp?.isCrossSite,
    ]);

    useEffect(() => {
        setProcessing(isLoading || fetchStatusOnMount);
    }, [fetchStatusOnMount, isLoading]);

    useEffect(() => {
        setCurrentStatus(initialStatus);
    }, [initialStatus]);

    const handleRsvpStatusChange = async (newStatus: RsvpStatus) => {
        const action = newStatus === RsvpStatus.CONFIRMED ? "accept-rsvp" : "delete-rsvp";
        setProcessing(true);
        const errorMessage = translate("Something went wrong. We could not update your response.");
        try {
            const rsvpResult = await postKmsData(`${baseUrl}/rsvp/index/${action}`, { ks, entryId: entry.id });
            setProcessing(false);

            const successMessage =
                newStatus === RsvpStatus.CONFIRMED
                    ? translate("You're in! '%1' has been added to your schedule", [entry.name])
                    : translate("Sorry you can't make it! We've noted your cancellation.");

            // Show toast only when not redirecting to login page
            if (!rsvpResult.redirect) {
                setCurrentStatus(newStatus);
                showToast({
                    severity: rsvpResult.success ? "success" : "error",
                    message: rsvpResult.success ? successMessage : errorMessage,
                });
                onChange?.(newStatus);
            }

            // Redirect to login page if not logged in
            if (!rsvpResult.success && rsvpResult.redirect) {
                window.location.href = `${rsvpResult.redirect}?ref=${baseUrl}${encodeURIComponent(window.location.pathname + window.location.search)}`;
                return;
            }
        }
        catch (e) {
            setProcessing(false);
            showToast({ severity: "error", message: errorMessage });
        }
    };

    const toggleRsvpStatus = () => {
        onToggleClicked?.();
        const newStatus = currentStatus === RsvpStatus.UNCONFIRMED ? RsvpStatus.CONFIRMED : RsvpStatus.UNCONFIRMED;
        handleRsvpStatusChange(newStatus);
        const analyticsName =
            newStatus === RsvpStatus.CONFIRMED
                ? `RSVP to session${analyticsValue?.location ? ` - ${analyticsValue.location}` : ""}`
                : `Remove RSVP to session${analyticsValue?.location ? ` - ${analyticsValue.location}` : ""}`;
        sendAnalytics(ButtonClickAnalyticsType.REGISTER, analyticsName);
    };

    const rsvpHoverHandler = () => {
        if (currentStatus === RsvpStatus.CONFIRMED) {
            setButtonLabel(cancelRsvpLabel);
        }
    };

    const rsvpMouseLeaveHandler = () => {
        setButtonLabel(textIconMappingByStatus[currentStatus].label);
    };

    const splitButtonMenuItems = useMemo(() => {
        const resendPostRsvp = async () => {
            sendAnalytics(ButtonClickAnalyticsType.SEND, `${analyticsValue?.location} iCal - resend email`);

            try {
                const result = await postKmsData(`${baseUrl}/rsvp/index/resend-rsvp-ical`, { entryId: entry.id });
                if (result.success) {
                    showToast({ severity: "success", message: result.message });
                }
                else {
                    showToast({ severity: "error", message: result.error });
                }
            }
            catch (e) {
                showToast({ severity: "error", message: translate("Error sending calendar email") });
            }
        };

        if (config?.rsvp?.allowDownload) {
            return Children.toArray(
                <AddToCalendarMenuItems
                    calendarEntriesToken={calendarEntriesToken}
                    entryId={entry.id}
                    ks={ks}
                    analyticsValue={{ location: analyticsValue?.location ?? "", module: "RSVP" }}
                />
            ) as ReactElement<MenuItemProps>[];
        }

        if (config?.rsvp?.isPostRsvpEmailEnabled && allowResendPostRsvp && currentStatus === RsvpStatus.CONFIRMED) {
            return [
                <StyledMenuItem component="a" onClick={resendPostRsvp}>
                    <Mail24Icon /> {translate("Resend iCal Invitation")}
                </StyledMenuItem>,
            ] as ReactElement<MenuItemProps>[];
        }

        return null;
    }, [
        allowResendPostRsvp,
        config?.rsvp?.allowDownload,
        config?.rsvp?.isPostRsvpEmailEnabled,
        currentStatus,
        entry.id,
        showToast,
    ]);

    const sendButtonAnalytics = useButtonAnalytics();
    const sendAnalytics = (type: ButtonClickAnalyticsType, name: string) => {
        sendButtonAnalytics(name, type, entry.id, "RSVP", "RSVP", false, ks);
    };

    // Compute the label length to reserve space.
    // When rsvp confirmed we pick the longest between "RSVP accepted" and "Cancel RSVP" to avoid resizing when labels swap.
    // When rsvp unconfirmed or loading we pick the length of rsvpLabel to keep loading state with the same size as rsvp button (per requirement).
    const longestHoverLabel = [rsvpAcceptedLabel, cancelRsvpLabel].reduce((a, b) => (b.length > a.length ? b : a), "");
    const btnLabelLength =
        currentStatus === RsvpStatus.UNCONFIRMED || processing ? rsvpLabel.length : longestHoverLabel.length;

    return splitButtonMenuItems ? (
        <StyledSplitButton
            className={`${baseClassName}-split-root`}
            maxLabelChars={btnLabelLength}
            loading={processing}
            buttonGroupAreaLabel={`${textIconMappingByStatus[currentStatus].label} - ${entry.name}`}
            size={size}
            buttonText={buttonLabel}
            startIcon={textIconMappingByStatus[currentStatus].icon}
            onClick={toggleRsvpStatus}
            menuItems={splitButtonMenuItems}
            actionButtonProps={{
                onMouseEnter: rsvpHoverHandler,
                onFocus: rsvpHoverHandler,
                onMouseLeave: rsvpMouseLeaveHandler,
                onBlur: rsvpMouseLeaveHandler,
                className: `${baseClassName}-split-main`,
            }}
        />
    ) : (
        <StyledButton
            className={baseClassName}
            maxLabelChars={btnLabelLength}
            loading={processing}
            aria-label={`${textIconMappingByStatus[currentStatus].label} - ${entry.name}`}
            size={size}
            startIcon={textIconMappingByStatus[currentStatus].icon}
            onClick={toggleRsvpStatus}
            onMouseEnter={rsvpHoverHandler}
            onFocus={rsvpHoverHandler}
            onMouseLeave={rsvpMouseLeaveHandler}
            onBlur={rsvpMouseLeaveHandler}
        >
            {buttonLabel}
        </StyledButton>
    );
};
